import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import {
  dateTimeLocalFromWallClock,
  formatWallClockDateTime,
  getInvoiceDateValidationMessage,
  invoiceDateMaxDateTimeLocal,
  invoiceDateMinDateTimeLocal,
  toStoredWallClock,
  wallClockFromDateTimeLocal,
} from '../../../utils/invoiceDateTime';

const PICKER_FORMAT = 'MM/DD/YYYY hh:mm A';

/**
 * Accounting-only: when re-invoicing after uninvoice / unlock, choose whether
 * to keep the latest preserved invoice date and/or the proposal's preserved
 * invoice number, or pick/generate new values. Dates are timezone-agnostic
 * wall-clock values.
 */
const InvoiceDateChoiceDialog = ({
  open,
  originalInvoicedAt,
  originalInvoiceNumber,
  jobCreatedAt,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const showDateChoice = Boolean(originalInvoicedAt);
  const showNumberChoice = Boolean(originalInvoiceNumber);

  const [dateMode, setDateMode] = useState('original');
  const [numberMode, setNumberMode] = useState('original');
  const [editedValue, setEditedValue] = useState('');
  const [error, setError] = useState('');
  const [maxLocal, setMaxLocal] = useState(() => invoiceDateMaxDateTimeLocal());
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDateMode('original');
    setNumberMode('original');
    setEditedValue(dateTimeLocalFromWallClock(originalInvoicedAt));
    setError('');
    setMaxLocal(invoiceDateMaxDateTimeLocal());
    setPickerOpen(false);
  }, [open, originalInvoicedAt, originalInvoiceNumber]);

  const pickerDisabled = loading;
  const minValue = invoiceDateMinDateTimeLocal(jobCreatedAt);
  const originalDateLabel = originalInvoicedAt
    ? `Use original invoice date (${formatWallClockDateTime(originalInvoicedAt)})`
    : 'Use original invoice date';
  const originalNumberLabel = originalInvoiceNumber
    ? `Use original invoice number (${originalInvoiceNumber})`
    : 'Use original invoice number';

  const openPicker = () => {
    if (loading || !showDateChoice) return;
    setDateMode('edit');
    setMaxLocal(invoiceDateMaxDateTimeLocal());
    setPickerOpen(true);
  };

  const resolveInvoicedAt = () => {
    if (!showDateChoice) {
      return null;
    }
    if (dateMode === 'original') {
      const stored = toStoredWallClock(originalInvoicedAt);
      if (!stored) {
        setError('Original invoice date is missing');
        return undefined;
      }
      return stored;
    }

    const validationMessage = getInvoiceDateValidationMessage({
      dateTimeLocal: editedValue,
      jobCreatedAt,
      now: new Date(),
    });
    if (validationMessage) {
      setError(validationMessage);
      return undefined;
    }
    const stored = wallClockFromDateTimeLocal(editedValue);
    if (!stored) {
      setError('Enter a valid date and time');
      return undefined;
    }
    return stored;
  };

  const handleConfirm = () => {
    if (loading) return;

    const invoicedAt = resolveInvoicedAt();
    if (showDateChoice && invoicedAt === undefined) return;

    onConfirm?.({
      invoicedAt,
      forceNewInvoiceNumber: showNumberChoice && numberMode === 'new',
    });
  };

  const title = showDateChoice && showNumberChoice
    ? 'Invoice Date & Number'
    : showNumberChoice
      ? 'Invoice Number'
      : 'Invoice Date';

  const description = (() => {
    if (showDateChoice && showNumberChoice) {
      return 'This job already has an invoice date and this proposal already has an invoice number. Keep the originals, or choose a new date/time and/or generate a new number from the client pattern. The date and time you pick is saved as-is (no timezone conversion). It cannot be more than one day before the job was created, or more than one day in the future.';
    }
    if (showNumberChoice) {
      return 'This proposal already has an invoice number. Use the original number, or generate a new one from the client pattern based on invoices already in the database.';
    }
    return 'This job already has an invoice date. Use the original date, or choose a different date and time. The date and time you pick is saved as-is (no timezone conversion). It cannot be more than one day before the job was created, or more than one day in the future.';
  })();

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (loading) return;
        if (pickerOpen) {
          setPickerOpen(false);
          return;
        }
        onClose?.();
      }}
      disableEnforceFocus
      aria-labelledby="invoice-date-choice-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="invoice-date-choice-dialog-title">
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {description}
        </DialogContentText>

        {showDateChoice && (
          <>
            {showNumberChoice && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Invoice date
              </Typography>
            )}
            <FormControl component="fieldset" fullWidth disabled={loading}>
              <RadioGroup
                value={dateMode}
                onChange={(event) => {
                  setDateMode(event.target.value);
                  setError('');
                  setPickerOpen(false);
                }}
              >
                <FormControlLabel
                  value="original"
                  control={<Radio />}
                  label={originalDateLabel}
                />
                <FormControlLabel
                  value="edit"
                  control={<Radio />}
                  label="Edit invoice date"
                />
              </RadioGroup>
            </FormControl>
            <DateTimePicker
              label="Invoice date"
              format={PICKER_FORMAT}
              ampm
              enableAccessibleFieldDOMStructure={false}
              value={editedValue ? dayjs(editedValue) : null}
              onChange={(newValue) => {
                setDateMode('edit');
                setEditedValue(newValue && newValue.isValid() ? newValue.format('YYYY-MM-DDTHH:mm') : '');
                setError('');
              }}
              disabled={pickerDisabled}
              open={pickerOpen}
              onOpen={openPicker}
              onClose={() => setPickerOpen(false)}
              minDateTime={minValue ? dayjs(minValue) : undefined}
              maxDateTime={maxLocal ? dayjs(maxLocal) : undefined}
              sx={{ mt: 2, width: '100%', mb: showNumberChoice ? 3 : 0 }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  error: !!error,
                  helperText: error || (dateMode === 'edit' ? 'Saved as the date and time you see. Not converted by timezone.' : ' '),
                  onMouseDown: (event) => {
                    if (loading) return;
                    event.preventDefault();
                    setDateMode('edit');
                    setMaxLocal(invoiceDateMaxDateTimeLocal());
                    setPickerOpen(true);
                  },
                  sx: {
                    '& .MuiInputBase-root': {
                      cursor: loading ? 'default' : 'pointer',
                    },
                    '& .MuiInputBase-input': {
                      cursor: loading ? 'default' : 'pointer',
                    },
                  },
                  slotProps: {
                    htmlInput: {
                      readOnly: true,
                    },
                  },
                },
                popper: {
                  sx: { zIndex: 1500 },
                },
              }}
            />
          </>
        )}

        {showNumberChoice && (
          <>
            {showDateChoice && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Invoice number
              </Typography>
            )}
            <FormControl component="fieldset" fullWidth disabled={loading}>
              <RadioGroup
                value={numberMode}
                onChange={(event) => {
                  setNumberMode(event.target.value);
                }}
              >
                <FormControlLabel
                  value="original"
                  control={<Radio />}
                  label={originalNumberLabel}
                />
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Generate new invoice number"
                />
              </RadioGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Invoicing…' : 'Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDateChoiceDialog;
